/**
 * Page setup shared by the editor's Pages view and the exporters (PDF print window, Word).
 *
 * Paper sizes are measured at 96 CSS px per inch with 1 in margins on every side, so what the
 * Pages view shows is what the PDF and Word exports produce:
 *   Letter: 816 x 1056 px sheet, 624 x 864 px content area (8.5 x 11 in).
 *   A4:     794 x 1123 px sheet, 602 x 931 px content area (210 x 297 mm).
 *
 * The chosen size is a per-browser preference stored under localStorage
 * `quill:editor:paper-size` ('letter' | 'a4', default 'letter'). `writePaperSize` also fires a
 * `quill:paper-size-change` window event (detail: { paperSize }) so open editors follow along.
 *
 * Page modes (`normalizePageMode`): 'page' (continuous A4-width sheet, the default), 'wide'
 * (full width) and 'pages' (print layout: separate sheets with page numbers).
 */

export const PAPER_SIZE_STORAGE_KEY = 'quill:editor:paper-size';
export const PAPER_SIZE_EVENT = 'quill:paper-size-change';
export const DEFAULT_PAPER_SIZE = 'letter';

/** CSS px per inch. */
export const PX_PER_INCH = 96;
/** Page margin on every side (px): 1 in. */
export const PAGE_MARGIN_PX = PX_PER_INCH;
/** Docx twips per inch. */
const TWIPS_PER_INCH = 1440;

/**
 * Build a paper size spec.
 * @param {string} id - Id.
 * @param {string} label - Menu label.
 * @param {number} widthIn - Width (in).
 * @param {number} heightIn - Height (in).
 * @param {string} cssSize - CSS `@page size` keyword.
 * @param {{width: number, height: number}} twips - Exact docx page size (twips).
 * @returns {object} Frozen spec.
 */
const spec = (id, label, widthIn, heightIn, cssSize, twips) => {
  const width = Math.round(widthIn * PX_PER_INCH);
  const height = Math.round(heightIn * PX_PER_INCH);
  return Object.freeze({
    id,
    label,
    widthIn,
    heightIn,
    width,
    height,
    margin: PAGE_MARGIN_PX,
    marginIn: PAGE_MARGIN_PX / PX_PER_INCH,
    contentWidth: width - 2 * PAGE_MARGIN_PX,
    contentHeight: height - 2 * PAGE_MARGIN_PX,
    cssSize,
    twips: Object.freeze({ width: twips.width, height: twips.height, margin: TWIPS_PER_INCH }),
  });
};

/** Supported paper sizes, keyed by id. */
export const PAPER_SIZES = Object.freeze({
  letter: spec('letter', 'Letter', 8.5, 11, 'letter', { width: 12240, height: 15840 }),
  a4: spec('a4', 'A4', 210 / 25.4, 297 / 25.4, 'A4', { width: 11906, height: 16838 }),
});

/** Paper size ids in menu order. */
export const PAPER_SIZE_IDS = Object.freeze(['letter', 'a4']);

/** Page modes. */
export const PAGE_MODES = Object.freeze(['page', 'wide', 'pages']);
export const DEFAULT_PAGE_MODE = 'page';
/**
 * The editor view preference (documents and templates). Its own key, not part of the workspace
 * layout blob: Quill tabs still running an older build rewrite that blob and would turn an
 * unknown mode like 'pages' back into 'page' in every open tab.
 */
export const PAGE_MODE_STORAGE_KEY = 'quill:editor:page-mode';

/**
 * Read the stored view preference.
 * @returns {'page'|'wide'|'pages'|null} The stored mode, or null when none is stored.
 */
export function readPageMode() {
  try {
    const raw = window.localStorage.getItem(PAGE_MODE_STORAGE_KEY);
    return raw == null ? null : normalizePageMode(raw);
  } catch {
    return null;
  }
}

/**
 * Store the view preference (best-effort).
 * @param {unknown} mode - Page mode.
 * @returns {'page'|'wide'|'pages'} The normalized mode that was stored.
 */
export function writePageMode(mode) {
  const value = normalizePageMode(mode);
  try {
    window.localStorage.setItem(PAGE_MODE_STORAGE_KEY, value);
  } catch {
    /* storage unavailable */
  }
  return value;
}

/**
 * Coerce any value to a paper size id.
 * @param {unknown} value - Raw value ('A4', 'letter', null...).
 * @returns {'letter'|'a4'} Paper size id.
 */
export function normalizePaperSize(value) {
  const key = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return Object.prototype.hasOwnProperty.call(PAPER_SIZES, key) ? key : DEFAULT_PAPER_SIZE;
}

/**
 * Coerce any value to a page mode.
 * @param {unknown} value - Raw value.
 * @returns {'page'|'wide'|'pages'} Page mode.
 */
export function normalizePageMode(value) {
  return PAGE_MODES.includes(value) ? value : DEFAULT_PAGE_MODE;
}

/**
 * Spec for a paper size id (unknown ids fall back to Letter).
 * @param {unknown} id - Paper size id.
 * @returns {typeof PAPER_SIZES.letter} Spec.
 */
export function getPaperSize(id) {
  return PAPER_SIZES[normalizePaperSize(id)];
}

/**
 * Read the stored paper size preference (private windows may throw: default then).
 * @returns {'letter'|'a4'} Paper size id.
 */
export function readPaperSize() {
  try {
    return normalizePaperSize(window.localStorage.getItem(PAPER_SIZE_STORAGE_KEY));
  } catch {
    return DEFAULT_PAPER_SIZE;
  }
}

/**
 * Store the paper size preference (best-effort) and tell open editors.
 * @param {unknown} id - Paper size id.
 * @returns {'letter'|'a4'} The normalized id that was stored.
 */
export function writePaperSize(id) {
  const value = normalizePaperSize(id);
  try {
    window.localStorage.setItem(PAPER_SIZE_STORAGE_KEY, value);
  } catch {
    /* storage unavailable */
  }
  try {
    window.dispatchEvent(new CustomEvent(PAPER_SIZE_EVENT, { detail: { paperSize: value } }));
  } catch {
    /* no window events (non-browser) */
  }
  return value;
}

/**
 * Follow paper size changes made elsewhere (this tab through writePaperSize, other tabs through
 * the storage event).
 * @param {(paperSize: 'letter'|'a4') => void} callback - Called with the new id.
 * @returns {() => void} Unsubscribe.
 */
export function subscribePaperSize(callback) {
  if (typeof window === 'undefined' || typeof callback !== 'function') return () => {};
  const onLocal = (event) => callback(normalizePaperSize(event?.detail?.paperSize));
  const onStorage = (event) => {
    if (event.key === PAPER_SIZE_STORAGE_KEY) callback(normalizePaperSize(event.newValue));
  };
  window.addEventListener(PAPER_SIZE_EVENT, onLocal);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(PAPER_SIZE_EVENT, onLocal);
    window.removeEventListener('storage', onStorage);
  };
}
